import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createClient } from "@supabase/supabase-js";

function publicClient() {
  return createClient(
    process.env.SUPABASE_URL ?? import.meta.env.VITE_SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY ?? import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY!,
  );
}

export const listPublicVacancies = createServerFn({ method: "GET" }).handler(async () => {
  const sb = publicClient();
  const { data, error } = await sb
    .from("vacancies")
    .select("*")
    .eq("is_published", true)
    .order("posted_at", { ascending: false })
    .limit(200);
  if (error) throw new Error(error.message);
  return data ?? [];
});

export const getPublicVacancy = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: row, error } = await sb
      .from("vacancies")
      .select("*")
      .eq("id", data.id)
      .eq("is_published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return row;
  });

const upsertSchema = z.object({
  id: z.string().uuid().optional(),
  title: z.string().min(2),
  employer: z.string().min(2),
  location: z.string().optional().nullable(),
  sector: z.string().optional().nullable(),
  employment_type: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  requirements: z.string().optional().nullable(),
  application_url: z.string().url(),
  source_name: z.string().min(2),
  source_url: z.string().url().optional().nullable(),
  salary_range: z.string().optional().nullable(),
  closing_date: z.string().optional().nullable(),
  is_published: z.boolean().optional(),
});

async function assertAdmin(ctx: { supabase: any; userId: string }) {
  const { data, error } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden");
}

export const adminListVacancies = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { data, error } = await context.supabase
      .from("vacancies")
      .select("*")
      .order("posted_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const upsertVacancy = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => upsertSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const payload = { ...data, closing_date: data.closing_date || null };
    if (data.id) {
      const { error } = await context.supabase
        .from("vacancies")
        .update(payload)
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: row, error } = await context.supabase
      .from("vacancies")
      .insert(payload)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id };
  });

export const deleteVacancy = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { error } = await context.supabase.from("vacancies").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- AI: extract vacancy fields from an uploaded picture ----------

// Coerce nulls/arrays/numbers coming from the model into safe strings so
// Zod validation never throws on cosmetic type drift from the AI.
const asString = z.preprocess((v) => {
  if (v == null) return "";
  if (Array.isArray(v))
    return v
      .map((x) => (x == null ? "" : typeof x === "string" ? x : String(x)))
      .filter(Boolean)
      .join("\n");
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return "";
    }
  }
  return "";
}, z.string().default(""));

const asStringArray = z.preprocess((v) => {
  if (v == null) return [];
  if (Array.isArray(v))
    return v.map((x) => (typeof x === "string" ? x : String(x ?? ""))).filter(Boolean);
  if (typeof v === "string") return v ? [v] : [];
  return [];
}, z.array(z.string()).default([]));

const asConfidence = z.preprocess(
  (v) => {
    if (v == null || v === "") return 0.5;
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(n)) return 0.5;
    return Math.max(0, Math.min(1, n));
  },
  z.number().min(0).max(1).default(0.5),
);

const ExtractShape = z.object({
  title: asString,
  employer: asString,
  location: asString,
  sector: asString,
  employment_type: asString,
  salary_range: asString,
  closing_date: asString, // ISO date if visible
  application_url: asString,
  source_name: asString,
  source_url: asString,
  description: asString,
  requirements: asString,
  confidence: asConfidence,
  warnings: asStringArray,
});

export type ExtractedVacancy = z.infer<typeof ExtractShape>;

const ALLOWED_IMAGE_MIMES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);
const MAX_IMAGE_BASE64 = 16_000_000; // ~12 MB decoded

const ExtractInput = z.object({
  fileName: z.string().max(200),
  fileMime: z
    .string()
    .max(120)
    .refine(
      (m) => ALLOWED_IMAGE_MIMES.has(m.toLowerCase()),
      "Please upload a JPG, PNG, WEBP, or GIF image.",
    ),
  fileBase64: z
    .string()
    .max(MAX_IMAGE_BASE64, "Image is too large — please keep it under 12 MB.")
    .refine(
      (b) => !/^TVqQAA|^TVoAAA|^f0VMR/.test(b.slice(0, 32)),
      "Executable files are not allowed.",
    ),
});

export const adminExtractVacancyFromImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => ExtractInput.parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("AI not configured");

    const { auditUpload, heuristicScan, scanUpload } = await import(
      "./_upload-audit.server"
    );
    const fileSize = Math.floor((data.fileBase64.length * 3) / 4);
    const baseAudit = {
      userId: context.userId,
      surface: "vacancy_extractor",
      fileName: data.fileName,
      fileMime: data.fileMime,
      fileSizeBytes: fileSize,
    };
    await auditUpload({ ...baseAudit, action: "upload_received" });

    if (!data.fileMime.startsWith("image/")) {
      await auditUpload({
        ...baseAudit,
        action: "validation_failed",
        errorCode: "bad_mime",
        errorMessage: data.fileMime,
      });
      throw new Error("Please upload an image (JPG/PNG/WEBP).");
    }

    const heur = heuristicScan(data.fileMime, data.fileBase64);
    if (!heur.ok) {
      await auditUpload({
        ...baseAudit,
        action: "validation_failed",
        verdict: "heuristic_blocked",
        errorCode: heur.code,
        errorMessage: heur.message,
      });
      throw new Error(`Upload rejected: ${heur.message}`);
    }
    await auditUpload({ ...baseAudit, action: "scan_started" });
    const scan = await scanUpload(data.fileMime, data.fileBase64, data.fileName);
    await auditUpload({
      ...baseAudit,
      action: scan.verdict === "scan_failed" ? "scan_failed" : "scan_completed",
      verdict: scan.verdict,
      scanProvider: scan.provider,
      scanRef: scan.ref,
      errorMessage: scan.verdict === "scan_failed" ? scan.message : null,
    });
    if (scan.verdict === "infected" || scan.verdict === "suspicious") {
      throw new Error(
        `Upload blocked by malware scan (${scan.verdict}). Please upload a clean image.`,
      );
    }
    if (scan.verdict === "scan_failed") {
      throw new Error(
        "Malware scan failed and could not confirm the file is clean. Please try again shortly.",
      );
    }


    const sys =
      "You extract structured vacancy details from a poster/advertisement image for South African audiences. Return ONLY minified JSON matching the schema. Do not invent facts — leave a field empty if not clearly visible, and add a warning.";
    const instructions = `Read the vacancy poster and populate:
- title, employer, location, sector, employment_type (Full-time/Part-time/Contract/Internship/Learnership/Temporary),
- salary_range (verbatim if shown),
- closing_date as YYYY-MM-DD if a date is visible,
- application_url (only if a URL is clearly visible/legible),
- source_name (issuing body, e.g. "DPSA Circular 42 of 2026" or employer),
- source_url if visible,
- description (2-4 sentences summarising the role),
- requirements (bullet-style list joined by newlines),
- confidence 0-1 based on legibility,
- warnings: list any illegible fields, missing contact info, or signs of a scam (unrealistic salary, personal email, upfront fee).
Only return the JSON object.`;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 60_000);
    let res: Response;
    try {
      res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Lovable-API-Key": key },
        body: JSON.stringify({
          model: "google/gemini-2.5-flash",
          messages: [
            { role: "system", content: sys },
            {
              role: "user",
              content: [
                { type: "text", text: instructions },
                {
                  type: "image_url",
                  image_url: { url: `data:${data.fileMime};base64,${data.fileBase64}` },
                },
              ],
            },
          ],
          response_format: { type: "json_object" },
        }),
        signal: controller.signal,
      });
    } catch (e: any) {
      clearTimeout(timer);
      if (e?.name === "AbortError") throw new Error("AI took too long — try a clearer image.");
      throw e;
    }
    clearTimeout(timer);

    if (res.status === 429) throw new Error("AI is busy — try again in a moment.");
    if (res.status === 402) throw new Error("AI credits exhausted — contact the team.");
    if (!res.ok) {
      const detail = await res.text().catch(() => "");
      throw new Error(`AI gateway error ${res.status}${detail ? `: ${detail.slice(0, 300)}` : ""}`);
    }

    const payload = (await res.json()) as { choices?: Array<{ message?: { content?: string } }> };
    const raw = payload.choices?.[0]?.message?.content?.trim() ?? "";
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      const m = raw.match(/\{[\s\S]*\}/);
      if (!m) throw new Error("AI returned invalid JSON — try again.");
      parsed = JSON.parse(m[0]);
    }
    const safe = ExtractShape.safeParse(parsed);
    if (safe.success) return safe.data;
    // Last-resort: coerce to empty defaults so the admin can still edit manually.
    return ExtractShape.parse({});
  });
